/**
 * Contractor Daily Anchor (P10)
 * ─────────────────────────────
 * A single-line contextual header for the Contractor Portal Home tab.
 *
 * Hard rules (locked):
 *   • Deterministic. No randomness. Always returns exactly one message.
 *   • Read-only. No mutations. No new persistence.
 *   • No customer-revenue read (payout snapshots only via P8 selectors).
 *   • Reuses canonical resolvers only (P8 commandCenter selectors).
 *   • Priority ladder (highest first):
 *        1. urgent   — pending offer expiring (any pending offer)
 *        2. today    — jobs scheduled today
 *        3. action   — unscheduled assigned jobs
 *        4. earnings — upcomingTentative > 0
 *        5. calm     — empty state
 *   • Action scope is unscheduled-only for P10.
 *   • No fabricated install times.
 */

import type { Job } from "@/data/types";
import type { MarketplaceOffer } from "./marketplaceOffer";
import type { Contractor } from "@/data/types";
import {
  filterContractorJobs,
  filterContractorPendingOffers,
  computeContractorEarnings,
  getJobCompletionDate,
} from "./commandCenter";


export type DailyAnchorTone =
  | "urgent"
  | "today"
  | "action"
  | "earnings"
  | "calm";

export interface DailyAnchor {
  greeting: string;
  message: string;
  tone: DailyAnchorTone;
  /** 1 = highest. */
  priority: number;
}

/* ── Greeting ─────────────────────────────────────────────────────── */

export function resolveGreeting(firstName: string | undefined, now: Date): string {
  const hour = now.getHours();
  const period =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const trimmed = firstName?.trim();
  return trimmed ? `${period}, ${trimmed}` : period;
}

/* ── Money formatting (no decimals, US-locale, $-prefixed) ────────── */

function formatMoney(n: number): string {
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

/* ── Inputs ───────────────────────────────────────────────────────── */

export interface DailyAnchorInputs {
  contractors: Contractor[];
  jobs: Job[];
  offers: MarketplaceOffer[];
  now?: Date;
}

/**
 * Pure resolver — testable without React context.
 */
export function resolveDailyAnchor(
  contractorId: string,
  inputs: DailyAnchorInputs,
): DailyAnchor {
  const { contractors, jobs, offers, now = new Date() } = inputs;

  // Identity → greeting. Direct lookup against requested id (resolver is now
  // session-only and would return unresolved without a session — we don't
  // want anchor resolution to depend on the local browser session).
  const record = contractors.find((c) => c.id === contractorId);
  const firstName =
    record?.contactFirstName?.trim() ||
    record?.contactName?.trim().split(/\s+/)[0] ||
    undefined;

  const greeting = resolveGreeting(firstName, now);
  const matchKey = record?.companyName ?? "";

  const myJobs = filterContractorJobs(jobs, contractorId, matchKey);
  const myPending = filterContractorPendingOffers(offers, contractorId, now);

  // ── 1. URGENT ─────────────────────────────────────────────────────
  if (myPending.length > 0) {
    if (myPending.length === 1) {
      const ms = new Date(myPending[0].expiresAt).getTime() - now.getTime();
      const minutes = Math.max(0, Math.round(ms / 60000));
      const window =
        minutes < 60
          ? `${minutes}m`
          : `${Math.round(minutes / 60)}h`;
      return {
        greeting,
        message: `1 offer expires in ${window}`,
        tone: "urgent",
        priority: 1,
      };
    }
    return {
      greeting,
      message: `${myPending.length} offers expiring`,
      tone: "urgent",
      priority: 1,
    };
  }

  // ── 2. TODAY ──────────────────────────────────────────────────────
  const todayIso = now.toISOString().slice(0, 10);
  const todaysJobs = myJobs.filter(
    (j) => j.scheduledInstallDate === todayIso && !getJobCompletionDate(j),
  );
  if (todaysJobs.length > 0) {
    if (todaysJobs.length === 1) {
      const city = todaysJobs[0].address?.city?.trim();
      return {
        greeting,
        message: city ? `first stop in ${city}` : "first stop today",
        tone: "today",
        priority: 2,
      };
    }
    return {
      greeting,
      message: `you've got ${todaysJobs.length} installs today`,
      tone: "today",
      priority: 2,
    };
  }

  // ── 3. ACTION (unscheduled-only for P10) ──────────────────────────
  const unscheduled = myJobs.filter(
    (j) => !j.scheduledInstallDate && !getJobCompletionDate(j),
  );
  if (unscheduled.length > 0) {
    const n = unscheduled.length;
    return {
      greeting,
      message: `schedule needed for ${n} job${n === 1 ? "" : "s"}`,
      tone: "action",
      priority: 3,
    };
  }

  // ── 4. EARNINGS (upcoming only) ───────────────────────────────────
  const earnings = computeContractorEarnings(myJobs, offers, now);
  if (earnings.upcomingTentative > 0) {
    return {
      greeting,
      message: `${formatMoney(earnings.upcomingTentative)} lined up this week`,
      tone: "earnings",
      priority: 4,
    };
  }

  // ── 5. CALM ───────────────────────────────────────────────────────
  return {
    greeting,
    message: "you're clear today",
    tone: "calm",
    priority: 5,
  };
}

/**
 * Convenience for callers that have the full app context.
 */
export function getDailyAnchor(
  contractorId: string,
  inputs: DailyAnchorInputs,
): DailyAnchor {
  return resolveDailyAnchor(contractorId, inputs);
}
